// src/pages/labita/ytd/YTD.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Link as MuiLink,
    IconButton,
    Stack,
    Snackbar,
    Button,
    Checkbox,
    FormControlLabel,
    Popover,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider
} from "@mui/material";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Styles for TableHead Cells
const verticalHeaderCellStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    position: 'sticky',
    backgroundColor: '#f5f5f5',
    zIndex: 3,
    left: 0,
    minWidth: '180px',
    textAlign: 'left',
    fontWeight: 'bold',
    padding: '12px 16px'
};

const horizontalHeaderCellStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    position: 'sticky',
    backgroundColor: 'white',
    zIndex: 2,
    top: 0,
    textAlign: 'center',
    fontWeight: 'bold',
    padding: '12px 8px',
    minWidth: '120px'
};

const totalHeaderCellStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    position: 'sticky',
    backgroundColor: '#1976d2',
    zIndex: 2,
    top: 0,
    textAlign: 'center',
    fontWeight: 'bold',
    padding: '12px 8px',
    minWidth: '120px',
    color: 'white'
};

// Bulan untuk header horizontal
const BULAN = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

// Header vertikal
const VERTICAL_HEADERS = [
    'BUDGET',
    'REAL TANAM',
    'SELESAI TANAM',
    'FORECAST TANAM'
];

// Helper function untuk parse tanggal dari format DD-MM-YYYY
const parseDateFromDDMMYYYY = (dateStr) => {
    if (!dateStr || dateStr === '-' || dateStr === '') return null;
    
    try {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Bulan dimulai dari 0
            const year = parseInt(parts[2], 10);
            
            if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                return new Date(year, month, day);
            }
        }
    } catch (error) {
        console.error('Error parsing date:', error);
    }
    
    return null;
};

function YTD() {
    const { user, loading: authLoading } = useAuthCheck();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [loadingData, setLoadingData] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [filterAnchor, setFilterAnchor] = useState(null);
    const [selectedYears, setSelectedYears] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const [masterBudgetData, setMasterBudgetData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch data dari db_labita/db_master_budget_tanam
                const budgetTanamRef = ref(database, "db_labita/db_master_budget_tanam");
                const budgetTanamSnapshot = await onValuePromise(budgetTanamRef);
                const budgetTanamData = budgetTanamSnapshot.val();
                
                // Simpan data ke state
                const loadedData = [];
                if (budgetTanamData) {
                    Object.keys(budgetTanamData).forEach(key => {
                        loadedData.push({ 
                            firebaseKey: key, 
                            ...budgetTanamData[key]
                        });
                    });
                }
                setMasterBudgetData(loadedData);
                
                // Tentukan tahun yang akan ditampilkan: 2 tahun kebelakang dan 2 tahun ke depan dari tahun saat ini
                const currentYear = new Date().getFullYear();
                const yearsArray = [];
                
                // Tambahkan 2 tahun ke depan
                for (let year = currentYear + 2; year >= currentYear; year--) {
                    yearsArray.push(year);
                }
                
                // Tambahkan 2 tahun kebelakang
                for (let year = currentYear - 1; year >= currentYear - 2; year--) {
                    yearsArray.push(year);
                }
                
                // Urutkan dari yang terbaru ke terlama (descending)
                yearsArray.sort((a, b) => b - a);
                
                setAvailableYears(yearsArray);
                
                // Default select tahun saat ini
                setSelectedYears([currentYear]);

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterClick = (event) => {
        setFilterAnchor(event.currentTarget);
    };

    const handleFilterClose = () => {
        setFilterAnchor(null);
    };

    const handleYearToggle = (year) => {
        setSelectedYears(prev => {
            if (prev.includes(year)) {
                return prev.filter(y => y !== year);
            } else {
                return [...prev, year];
            }
        });
    };

    const handleSelectAllYears = () => {
        if (selectedYears.length === availableYears.length) {
            setSelectedYears([]);
        } else {
            setSelectedYears([...availableYears]);
        }
    };

    const handleApplyFilter = () => {
        if (selectedYears.length === 0) {
            setSnackbar({
                open: true,
                message: "Pilih minimal satu tahun untuk filter",
                severity: "warning",
            });
            return;
        }

        handleFilterClose();
        
        setSnackbar({
            open: true,
            message: `Filter diterapkan untuk tahun: ${selectedYears.join(', ')}`,
            severity: "success",
        });
    };

    const handleResetFilter = () => {
        const currentYear = new Date().getFullYear();
        setSelectedYears([currentYear]);
        
        setSnackbar({
            open: true,
            message: "Filter direset ke tahun saat ini",
            severity: "info",
        });
    };

    // Helper function untuk format angka dengan pemisah ribuan
    const formatNumberWithSeparator = (value) => {
        if (value === undefined || value === null || value === '' || value === '-') {
            return '-';
        }
        
        if (typeof value === 'string') {
            if (value === '-') return '-';
            
            const num = parseFloat(value.replace(/,/g, '.'));
            if (!isNaN(num)) {
                return num.toLocaleString('id-ID', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            }
        }
        
        if (typeof value === 'number') {
            return value.toLocaleString('id-ID', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        }
        
        return value;
    };

    // Fungsi untuk menghitung nilai per bulan untuk BUDGET FRESHPINE (sts = "nf") - menggunakan netto
    const calculateFreshpineBudgetPerMonth = (bulanIndex) => {
        if (masterBudgetData.length === 0 || selectedYears.length === 0) {
            return 0;
        }

        let totalNetto = 0;
        
        masterBudgetData.forEach(item => {
            // Cek apakah sts = "nf" (case insensitive)
            if (item.sts && item.sts.toString().toLowerCase() === 'nf') {
                
                // Cek berdasarkan tanggal_rencana_tanam
                if (item.tanggal_rencana_tanam && item.tanggal_rencana_tanam !== '-') {
                    const date = parseDateFromDDMMYYYY(item.tanggal_rencana_tanam);
                    if (date) {
                        const year = date.getFullYear();
                        const month = date.getMonth(); // 0 = Januari, 11 = Desember
                        
                        // Cek apakah tahun sesuai dengan filter dan bulan sesuai dengan index
                        if (selectedYears.includes(year) && month === bulanIndex) {
                            // Jumlahkan netto jika ada
                            if (item.netto && item.netto !== '-') {
                                const nettoValue = parseFloat(item.netto.toString().replace(/,/g, '.'));
                                if (!isNaN(nettoValue)) {
                                    totalNetto += nettoValue;
                                }
                            }
                        }
                    }
                }
            }
        });
        
        return totalNetto;
    };

    // Fungsi untuk menghitung total BUDGET FRESHPINE untuk semua bulan
    const calculateTotalFreshpineBudget = () => {
        if (masterBudgetData.length === 0 || selectedYears.length === 0) {
            return 0;
        }

        let totalNetto = 0;
        
        masterBudgetData.forEach(item => {
            // Cek apakah sts = "nf" (case insensitive)
            if (item.sts && item.sts.toString().toLowerCase() === 'nf') {
                
                // Cek berdasarkan tanggal_rencana_tanam
                if (item.tanggal_rencana_tanam && item.tanggal_rencana_tanam !== '-') {
                    const date = parseDateFromDDMMYYYY(item.tanggal_rencana_tanam);
                    if (date) {
                        const year = date.getFullYear();
                        
                        // Cek apakah tahun sesuai dengan filter
                        if (selectedYears.includes(year)) {
                            // Jumlahkan netto jika ada
                            if (item.netto && item.netto !== '-') {
                                const nettoValue = parseFloat(item.netto.toString().replace(/,/g, '.'));
                                if (!isNaN(nettoValue)) {
                                    totalNetto += nettoValue;
                                }
                            }
                        }
                    }
                }
            }
        });
        
        return totalNetto;
    };

    // Fungsi untuk menghitung nilai per bulan untuk BUDGET BANANA (sts = "ps") - menggunakan aktif
    const calculateBananaBudgetPerMonth = (bulanIndex) => {
        if (masterBudgetData.length === 0 || selectedYears.length === 0) {
            return 0;
        }

        let totalAktif = 0;
        
        masterBudgetData.forEach(item => {
            // Cek apakah sts = "ps" (case insensitive)
            if (item.sts && item.sts.toString().toLowerCase() === 'ps') {
                
                // Cek berdasarkan tanggal_rencana_tanam
                if (item.tanggal_rencana_tanam && item.tanggal_rencana_tanam !== '-') {
                    const date = parseDateFromDDMMYYYY(item.tanggal_rencana_tanam);
                    if (date) {
                        const year = date.getFullYear();
                        const month = date.getMonth(); // 0 = Januari, 11 = Desember
                        
                        // Cek apakah tahun sesuai dengan filter dan bulan sesuai dengan index
                        if (selectedYears.includes(year) && month === bulanIndex) {
                            // Jumlahkan aktif jika ada
                            if (item.aktif && item.aktif !== '-') {
                                const aktifValue = parseFloat(item.aktif.toString().replace(/,/g, '.'));
                                if (!isNaN(aktifValue)) {
                                    totalAktif += aktifValue;
                                }
                            }
                        }
                    }
                }
            }
        });
        
        return totalAktif;
    };

    // Fungsi untuk menghitung total BUDGET BANANA untuk semua bulan
    const calculateTotalBananaBudget = () => {
        if (masterBudgetData.length === 0 || selectedYears.length === 0) {
            return 0;
        }

        let totalAktif = 0;
        
        masterBudgetData.forEach(item => {
            // Cek apakah sts = "ps" (case insensitive)
            if (item.sts && item.sts.toString().toLowerCase() === 'ps') {
                
                // Cek berdasarkan tanggal_rencana_tanam
                if (item.tanggal_rencana_tanam && item.tanggal_rencana_tanam !== '-') {
                    const date = parseDateFromDDMMYYYY(item.tanggal_rencana_tanam);
                    if (date) {
                        const year = date.getFullYear();
                        
                        // Cek apakah tahun sesuai dengan filter
                        if (selectedYears.includes(year)) {
                            // Jumlahkan aktif jika ada
                            if (item.aktif && item.aktif !== '-') {
                                const aktifValue = parseFloat(item.aktif.toString().replace(/,/g, '.'));
                                if (!isNaN(aktifValue)) {
                                    totalAktif += aktifValue;
                                }
                            }
                        }
                    }
                }
            }
        });
        
        return totalAktif;
    };

    // Fungsi untuk menghitung nilai YTD untuk FRESHPINE
    const calculateFreshpineYTDValue = (headerType, bulanIndex) => {
        // Jika tidak ada data atau tidak ada tahun yang dipilih
        if (masterBudgetData.length === 0 || selectedYears.length === 0) {
            return '-';
        }

        // Untuk BUDGET: sts = "nf" dan tahun sesuai filter
        if (headerType === 'BUDGET') {
            const totalNetto = calculateFreshpineBudgetPerMonth(bulanIndex);
            return totalNetto > 0 ? formatNumberWithSeparator(totalNetto) : '-';
        }
        
        // Untuk header lainnya, tetap menggunakan data dummy (sementara)
        const dummyData = {
            'REAL TANAM': [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            'SELESAI TANAM': [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            'FORECAST TANAM': [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
        };

        const value = dummyData[headerType]?.[bulanIndex] || 0;
        return value.toLocaleString('id-ID');
    };

    // Fungsi untuk menghitung nilai YTD untuk BANANA
    const calculateBananaYTDValue = (headerType, bulanIndex) => {
        // Jika tidak ada data atau tidak ada tahun yang dipilih
        if (masterBudgetData.length === 0 || selectedYears.length === 0) {
            return '-';
        }

        // Untuk BUDGET: sts = "ps" dan tahun sesuai filter
        if (headerType === 'BUDGET') {
            const totalAktif = calculateBananaBudgetPerMonth(bulanIndex);
            return totalAktif > 0 ? formatNumberWithSeparator(totalAktif) : '-';
        }
        
        // Untuk header lainnya, gunakan data dummy yang berbeda untuk BANANA
        const dummyData = {
            'REAL TANAM': [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            'SELESAI TANAM': [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            'FORECAST TANAM': [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
        };

        const value = dummyData[headerType]?.[bulanIndex] || 0;
        return value.toLocaleString('id-ID');
    };

    // Fungsi untuk menghitung total per header untuk FRESHPINE
    const calculateTotalForFreshpineHeader = (headerType) => {
        if (headerType === 'BUDGET') {
            const total = calculateTotalFreshpineBudget();
            return total > 0 ? formatNumberWithSeparator(total) : '-';
        }
        
        // Untuk header lainnya, hitung total dari data dummy FRESHPINE
        const dummyData = {
            'REAL TANAM': [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000],
            'SELESAI TANAM': [300, 600, 900, 1200, 1500, 1800, 2100, 2400, 2700, 3000, 3300, 3600],
            'FORECAST TANAM': [700, 1400, 2100, 2800, 3500, 4200, 4900, 5600, 6300, 7000, 7700, 8400]
        };

        const monthlyData = dummyData[headerType] || [];
        const total = monthlyData.reduce((sum, value) => sum + value, 0);
        return total > 0 ? formatNumberWithSeparator(total) : '-';
    };

    // Fungsi untuk menghitung total per header untuk BANANA
    const calculateTotalForBananaHeader = (headerType) => {
        if (headerType === 'BUDGET') {
            const total = calculateTotalBananaBudget();
            return total > 0 ? formatNumberWithSeparator(total) : '-';
        }
        
        // Untuk header lainnya, hitung total dari data dummy BANANA
        const dummyData = {
            'REAL TANAM': [400, 800, 1200, 1600, 2000, 2400, 2800, 3200, 3600, 4000, 4400, 4800],
            'SELESAI TANAM': [200, 400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2200, 2400],
            'FORECAST TANAM': [600, 1200, 1800, 2400, 3000, 3600, 4200, 4800, 5400, 6000, 6600, 7200]
        };

        const monthlyData = dummyData[headerType] || [];
        const total = monthlyData.reduce((sum, value) => sum + value, 0);
        return total > 0 ? formatNumberWithSeparator(total) : '-';
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const totalFreshpineBudget = calculateTotalFreshpineBudget();
    const totalBananaBudget = calculateTotalBananaBudget();

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        YTD
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleFilterClick}
                        >
                            <FilterAltOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Filter Popover - terbuka saat icon filter diklik */}
                <Popover
                    open={Boolean(filterAnchor)}
                    anchorEl={filterAnchor}
                    onClose={handleFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <Box sx={{ p: 2, minWidth: 250 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Pilih Tahun
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedYears.length === availableYears.length}
                                    indeterminate={selectedYears.length > 0 && selectedYears.length < availableYears.length}
                                    onChange={handleSelectAllYears}
                                />
                            }
                            label="Pilih Semua"
                        />
                        
                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {availableYears.map((year) => (
                                <ListItem key={year} disablePadding>
                                    <ListItemButton onClick={() => handleYearToggle(year)} dense>
                                        <Checkbox
                                            edge="start"
                                            checked={selectedYears.includes(year)}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <ListItemText primary={year} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        
                        <Divider sx={{ my: 2 }} />
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={handleResetFilter}>
                                Reset
                            </Button>
                            <Button size="small" variant="contained" onClick={handleApplyFilter}>
                                Terapkan
                            </Button>
                        </Stack>
                    </Box>
                </Popover>

                {/* Info Tahun Terpilih */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="white">
                        Menampilkan data untuk tahun: <strong>{selectedYears.join(', ')}</strong>
                    </Typography>
                </Box>

                {/* Tabel 1: FRESHPINE */}
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
                    FRESHPINE (sts = "nf")
                </Typography>
                <TableContainer sx={{ maxHeight: 400, overflow: 'auto', mb: 3 }}>
                    <Table stickyHeader aria-label="freshpine ytd report table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={verticalHeaderCellStyle}>
                                    FRESHPINE
                                </TableCell>
                                
                                {BULAN.map((bulan, index) => (
                                    <TableCell 
                                        key={bulan}
                                        align="center"
                                        sx={horizontalHeaderCellStyle}
                                    >
                                        {bulan}
                                    </TableCell>
                                ))}
                                
                                <TableCell 
                                    align="center"
                                    sx={totalHeaderCellStyle}
                                >
                                    TOTAL
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {VERTICAL_HEADERS.map((header, index) => (
                                <TableRow key={`freshpine_${header}`}>
                                    <TableCell 
                                        sx={{
                                            ...verticalHeaderCellStyle,
                                            backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#f5f5f5',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 1
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                    
                                    {BULAN.map((bulan, bIndex) => (
                                        <TableCell 
                                            key={`freshpine_${header}_${bulan}`}
                                            align="center"
                                            sx={{
                                                border: '1px solid rgba(224, 224, 224, 1)',
                                                backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#f5f5f5',
                                                padding: '12px 8px'
                                            }}
                                        >
                                            {calculateFreshpineYTDValue(header, bIndex)}
                                        </TableCell>
                                    ))}
                                    
                                    <TableCell 
                                        align="center"
                                        sx={{
                                            border: '1px solid rgba(224, 224, 224, 1)',
                                            backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#f5f5f5',
                                            padding: '12px 8px',
                                            fontWeight: 'bold',
                                            color: header === 'BUDGET' ? '#1976d2' : 'inherit'
                                        }}
                                    >
                                        {calculateTotalForFreshpineHeader(header)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Tabel 2: BANANA */}
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: '#2e7d32' }}>
                    BANANA (sts = "ps")
                </Typography>
                <TableContainer sx={{ maxHeight: 400, overflow: 'auto', mb: 3 }}>
                    <Table stickyHeader aria-label="banana ytd report table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{
                                    ...verticalHeaderCellStyle,
                                    backgroundColor: '#f0f7f0'
                                }}>
                                    BANANA
                                </TableCell>
                                
                                {BULAN.map((bulan, index) => (
                                    <TableCell 
                                        key={`banana_${bulan}`}
                                        align="center"
                                        sx={{
                                            ...horizontalHeaderCellStyle,
                                            backgroundColor: '#f0f7f0'
                                        }}
                                    >
                                        {bulan}
                                    </TableCell>
                                ))}
                                
                                <TableCell 
                                    align="center"
                                    sx={{
                                        ...totalHeaderCellStyle,
                                        backgroundColor: '#2e7d32'
                                    }}
                                >
                                    TOTAL
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {VERTICAL_HEADERS.map((header, index) => (
                                <TableRow key={`banana_${header}`}>
                                    <TableCell 
                                        sx={{
                                            ...verticalHeaderCellStyle,
                                            backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#f5f5f5',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 1
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                    
                                    {BULAN.map((bulan, bIndex) => (
                                        <TableCell 
                                            key={`banana_${header}_${bulan}`}
                                            align="center"
                                            sx={{
                                                border: '1px solid rgba(224, 224, 224, 1)',
                                                backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#f5f5f5',
                                                padding: '12px 8px'
                                            }}
                                        >
                                            {calculateBananaYTDValue(header, bIndex)}
                                        </TableCell>
                                    ))}
                                    
                                    <TableCell 
                                        align="center"
                                        sx={{
                                            border: '1px solid rgba(224, 224, 224, 1)',
                                            backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#f5f5f5',
                                            padding: '12px 8px',
                                            fontWeight: 'bold',
                                            color: header === 'BUDGET' ? '#2e7d32' : 'inherit'
                                        }}
                                    >
                                        {calculateTotalForBananaHeader(header)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Summary Information */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                        <span style={{ color: '#1976d2', fontWeight: 'bold' }}>FRESHPINE:</span> BUDGET = Jumlah netto dengan sts = "nf" |
                        <span style={{ color: '#2e7d32', fontWeight: 'bold', marginLeft: '16px' }}>BANANA:</span> BUDGET = Jumlah aktif dengan sts = "ps" |
                        Satuan: Hektar (Ha) | Data diupdate terakhir: {new Date().toLocaleDateString('id-ID')}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                        Total FRESHPINE (netto): <strong style={{ color: '#1976d2' }}>{formatNumberWithSeparator(totalFreshpineBudget)} Ha</strong> |
                        Total BANANA (aktif): <strong style={{ color: '#2e7d32' }}>{formatNumberWithSeparator(totalBananaBudget)} Ha</strong> |
                        Grand Total: <strong>{formatNumberWithSeparator(totalFreshpineBudget + totalBananaBudget)} Ha</strong>
                    </Typography>
                </Box>
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default YTD;